import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  IPP_VENDORS,
  CREATE_IPP_VENDOR,
  UPDATE_IPP_VENDOR,
  DELETE_IPP_VENDOR,
} from '@graphql/vendorList.gql';
import {
  IppVendor,
  VendorListHookReturn,
  CreateVendorInput,
  UpdateVendorInput,
} from '@custom-types/vendor';
import { message } from 'antd';

export const useVendorList = (): VendorListHookReturn => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState<IppVendor[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageInfo, setPageInfo] = useState({
    currentPage: 1,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const [statusFilter, setStatusFilter] = useState<string[]>([]);

  const [saveFeedback, setSaveFeedback] = useState<{
    status: 'success' | 'error';
    message: string;
  } | null>(null);

  /** Load Vendor List */
  const { loading, error, refetch } = useQuery(IPP_VENDORS, {
    variables: { page: currentPage, limit: pageSize },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    onCompleted: (result) => {
      const vendorData = result?.ippVendorsPaginated;
      if (vendorData) {
        setData(vendorData.nodes || []);
        setTotalCount(vendorData.totalCount || 0);
        setPageInfo(vendorData.pageInfo || pageInfo);
      }
    },
    onError: () => {
      void message.error('Failed to load vendors');
    },
  });

  /** Mutations */
  const [createVendor, { loading: creating }] = useMutation(CREATE_IPP_VENDOR, { errorPolicy: 'all' });
  const [updateVendor, { loading: updatingVendor }] = useMutation(UPDATE_IPP_VENDOR, { errorPolicy: 'all' });
  const [deleteVendor, { loading: deleting }] = useMutation(DELETE_IPP_VENDOR, { errorPolicy: 'all' });

  /** Pagination */
  const handleRefetchError = () => message.error('Error changing page');

  const onChangePage = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size && size !== pageSize) setPageSize(size);
    refetch({ page, limit: size || pageSize }).catch(handleRefetchError);
  };

  const refetchData = () => {
    void refetch({ page: currentPage, limit: pageSize }).catch(() =>
      message.error('Error refreshing vendor list')
    );
  };

  /** CREATE Vendor */
  const handleCreateVendor = async (vendorData: CreateVendorInput) => {
    try {
      const result = await createVendor({ variables: { input: vendorData } });
      if (result.errors?.length) throw new Error(result.errors[0].message);

      const created = result.data?.createIppVendor;
      if (created) {
        setData((prev) => [created, ...prev]);
        setTotalCount((prev) => prev + 1);

        message.success(`Vendor "${created.vendorName}" created successfully`, 5);
        setSaveFeedback({ status: 'success', message: 'Vendor created successfully' });

        return { success: true, data: created };
      }

      throw new Error('No vendor returned from server');
    } catch (err: any) {
      const msg = err.graphQLErrors?.[0]?.message || err.message || 'Failed to create vendor';
      message.error(msg);
      setSaveFeedback({ status: 'error', message: msg });
      return { success: false, error: msg };
    }
  };

  /** UPDATE Vendor */
  const handleUpdateVendor = async (id: number, vendorData: UpdateVendorInput) => {
    try {
      const result = await updateVendor({ variables: { id, input: vendorData } });
      if (result.errors?.length) throw new Error(result.errors[0].message);

      const updated = result.data?.updateIppVendor;
      if (updated) {
        setData((prev) => prev.map((v) => (v.id === id ? { ...v, ...updated } : v)));

        message.success(`Vendor "${updated.vendorName}" updated successfully`, 5);
        setSaveFeedback({ status: 'success', message: 'Vendor updated successfully' });

        return { success: true, data: updated };
      }

      throw new Error('No vendor returned from server');
    } catch (err: any) {
      const msg = err.graphQLErrors?.[0]?.message || err.message || 'Failed to update vendor';
      message.error(msg);
      setSaveFeedback({ status: 'error', message: msg });
      return { success: false, error: msg };
    }
  };

  /** DELETE Vendor */
  const handleDeleteVendor = async (id: number) => {
    try {
      const { data: result } = await deleteVendor({ variables: { id } });

      // A `false` result means the server found nothing to delete (already
      // gone, or the delete was rejected server-side) without throwing —
      // treat that the same as a failure instead of reporting a fake success.
      if (!result?.deleteIppVendor) {
        const msg = 'Vendor could not be deleted';
        message.error(msg);
        setSaveFeedback({ status: 'error', message: msg });
        return { success: false, error: msg };
      }

      const remaining = data.filter((v) => v.id !== id);
      setData(remaining);
      setTotalCount((prev) => prev - 1);

      let newPage = currentPage;
      if (remaining.length === 0 && currentPage > 1) newPage = currentPage - 1;

      await refetch({ page: newPage, limit: pageSize });

      message.success('Vendor deleted successfully', 5);
      setSaveFeedback({ status: 'success', message: 'Vendor deleted successfully' });

      return { success: true };
    } catch (err: any) {
      const msg = err.graphQLErrors?.[0]?.message || err.message || 'Failed to delete vendor';
      message.error(msg);
      setSaveFeedback({ status: 'error', message: msg });
      return { success: false, error: msg };
    }
  };

  /** Expose hook */
  return {
    data,
    totalCount,
    loading,
    updating: creating || updatingVendor || deleting,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    statusFilter,
    handleStatusFilter: (statuses: string[]) => setStatusFilter(statuses),
    clearFilters: () => setStatusFilter([]),
    creating,
    updatingVendor,
    deleting,
    setUpdatedRecord: () => { },
    handleCreateVendor,
    handleUpdateVendor,
    handleDeleteVendor,
    refetch: refetchData,
    onChangePage,
    errorFromUseData: error,
    hasNextPage: pageInfo.hasNextPage,
    hasPreviousPage: pageInfo.hasPreviousPage,
    totalPages: pageInfo.totalPages,
    saveFeedback,
    setSaveFeedback,
  };
};
